// SkillsCard
import React from 'react';
import { AboutProps, IncompleteTag } from '..';
import { ColorName } from 'types/theme';
import {
  CREATOR_ENVIRONMENT_TAGS,
  CREATOR_EQUIPMENT_TAGS,
  DEFAULT_ABOUT_SKILLS_TEXT,
} from 'constants/profile';
import {
  CREATOR_CATEGORY_TAGS,
  CREATOR_SKILL_TAGS,
} from 'constants/registration';
import styles from './SkillsCard.module.sass';
import ROUTES from 'config/routes';
import Link from 'next/link';
import { trackEvent } from 'services/analytics';
import cn from 'classnames';
import { CreatorProfileType } from 'types/profile';

export type SkillsCardProps = Pick<
  AboutProps,
  | 'profileType'
  | 'socialCategories'
  | 'contentCategories'
  | 'environment'
  | 'equipment'
>;

const SkillsCard: React.FC<SkillsCardProps> = ({
  profileType,
  contentCategories,
  socialCategories,
  environment,
  equipment,
}) => {
  const trackEditProfileClick = () => {
    trackEvent('Creator Clicks Edit Profile Link');
  };

  const skillTags = CREATOR_SKILL_TAGS.filter(({ id }) =>
    contentCategories.includes(id)
  ).map(({ label }) => label);
  const categoryTags = CREATOR_CATEGORY_TAGS.filter(({ id }) =>
    socialCategories.includes(id)
  ).map(({ label }) => label);
  const equipmentTags = CREATOR_EQUIPMENT_TAGS.filter(({ id }) =>
    equipment.includes(id)
  ).map(({ label }) => label);
  const environmentTags = CREATOR_ENVIRONMENT_TAGS.filter(({ id }) =>
    environment.includes(id)
  ).map(({ label }) => label);

  return (
    <div className={styles.skillsCard}>
      {profileType === 'personal' && (
        <Link passHref href={ROUTES.AccountSettings.getPath()} legacyBehavior>
          <p
            onClick={trackEditProfileClick}
            id="skills"
            className={styles.editButton}
          >
            Edit
          </p>
        </Link>
      )}
      {/* Skills */}
      <SkillSection
        profileType={profileType}
        title="Skills"
        subtitle={DEFAULT_ABOUT_SKILLS_TEXT['skills']}
        tags={skillTags}
        color="secondaryBlue"
      />
      {/* Categories */}
      <SkillSection
        profileType={profileType}
        title="Categories"
        subtitle={DEFAULT_ABOUT_SKILLS_TEXT['categories']}
        tags={categoryTags}
        color="secondaryYellow"
      />
      {/* Environment */}
      <SkillSection
        profileType={profileType}
        title="Environment"
        subtitle={DEFAULT_ABOUT_SKILLS_TEXT['environment']}
        tags={environmentTags}
        color="secondaryGreen"
      />
      {/* Equipment */}
      <SkillSection
        profileType={profileType}
        title="Equipment"
        subtitle={DEFAULT_ABOUT_SKILLS_TEXT['equipment']}
        tags={equipmentTags}
        color="secondaryPurple"
      />
    </div>
  );
};

export default SkillsCard;

interface SkillSectionProps {
  profileType: CreatorProfileType;
  title: string;
  subtitle: string;
  tags: string[];
  color: Extract<
    ColorName,
    'secondaryPurple' | 'secondaryBlue' | 'secondaryGreen' | 'secondaryYellow'
  >;
}

const SkillSection: React.FC<SkillSectionProps> = ({
  profileType,
  title,
  subtitle,
  tags,
  color,
}) => {
  return (
    <div
      className={cn(styles.skillSection, {
        [styles.hide]: tags.length === 0 && profileType !== 'personal',
      })}
    >
      <div className={styles.cardTitleRow}>
        <h5>{title}</h5>
        {profileType === 'personal' && tags.length === 0 && <IncompleteTag />}
      </div>
      <p>{subtitle}</p>
      <div className={styles.tagRow}>
        {tags.map((tag) => (
          <div key={tag} style={{ background: styles[color] }}>
            {tag}
          </div>
        ))}
      </div>
    </div>
  );
};
